import { observable, action, makeObservable } from 'mobx';
import { localStorageHandler } from 'core/services';
import { BaasicTableSettingsViewStore, BaasicDropdownViewStore } from 'common/stores';
import { BaseListViewStore, ListCellData } from 'core/stores';
import OrderStore from './OrderStore';
import { OrderLineStore } from 'modules/order-line/stores';
import { LookupStore } from 'modules/lookup/stores';
import { lookupRouteName, sourceOfChange, channelType } from 'common/constants';
import {
	OrderLineService,
	ChannelService,
	ProductVirtualSkuService,
	ShipmentInfoService,
	ApplicationService,
} from 'common/data';
import {
	TableFilterUtility,
	checkForColumnFilter,
	checkForColumnWidth,
	RemoveFiltersModalUtility,
	notificationError,
	isWarehouse,
	isSuperAdminOrganization,
	excelDateToJSDate,
} from 'core/utils';
import {
	HyperlinkCellData,
	DateTimeCellData,
	MultipleCellData,
	BooleanCellData,
	BuyerRequestedCancelCellData,
	AddressValidationInfoErrorsCellData,
	QuantityPerSkuCellData,
} from 'core/stores';
import { TableFilterTemplate, TableFilterNumericTemplate, TableFilterDateTemplate } from 'themes/components';
import moment from 'moment';
import XLSX from 'xlsx';
import excelJS from 'exceljs';
import _ from 'lodash';
import { ApplicationStore } from 'platform/application/stores';
import { OrderImportForm } from 'modules/order/forms';
import { CancelUncancelResult, MarketplaceOrderOnHold, OrderImport, Replacement } from '../pages';
import { isSome } from 'core/utils';
import Cancel from '../pages/Cancel';

class MarketplaceOrderListViewStore extends BaseListViewStore {
	@observable change = false;
	@observable order = null;
	@observable selectedItem = null;
	@observable showOrderLines = true;
	isSuperAdmin = false;
	hasPiiAccess = false;
	isExpand = true;
	showTodayShipments;
	showUnlinkedOrders;
	selectedItem = null;
	localStorageWidthKey = 'marketplace-orders-column-width';
	@observable orderTypeOptions = [];
	@observable orderStatusOptions = [];
	orderImportForm = new OrderImportForm();

	constructor(rootStore) {
		super(rootStore, {
			listStore: new OrderStore(rootStore),
			queryParams: {
				embed: [
					'shippingCoreCountry',
					'shippingCoreCountryState',
					'channel',
					'channel.timezone',
					'channel.channelType',
					'confirmationType',
					'orderType',
					'orderLines',
					'orderLines.product',
					'orderLines.orderLineStatus',
					'orderStatus',
					'orderLines',
					'seller',
					'fulfillmentOrders',
					'cancelReason',
					'onHoldReason',
				],
				filter: localStorageHandler.get('marketplace-orders-filter'),
			},
			findFunction: 'findOrder',
			tableData: {
				columns: [
					{
						key: 'orderNumber',
						title: 'ORDERVIEW.ORDERNUMBER',
						propertyToCheck: 'onHoldReasonId',
						translation: 'ORDERVIEW.ONHOLD',
						dataCell: HyperlinkCellData,
						routeName: 'master.app.main.order.marketplace.list.detail',
						selected: true,
						expandable: true,
						width: 200,
						filter: null,
						locked: true,
						filterType: TableFilterTemplate,
						sortable: true,
						filterApiParams: {
							checkPartiallyFulfillMixedOrders: true,
						},
					},
					{
						key: 'customerServiceOrderNumber',
						title: 'ORDERVIEW.CUSTOMERSERVICEORDERNUMBER',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
					},
					{
						key: 'source',
						title: 'ORDERVIEW.CHANNELSOURCE',
						service: 'orderService',
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						getDistinctValues: true,
					},
					{
						key: 'warehouseInfo',
						title: 'ORDERVIEW.WAREHOUSEINFO',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
					},
					{
						key: 'channelOrderDate',
						title: 'ORDERVIEW.ORDERDATE',
						dataCell: DateTimeCellData,
						dateFormat: 'LLL',
						useLocalTime: false,
						dontConvertTime: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						sortable: true,
					},
					{
						key: 'channelShipByDate',
						title: 'ORDERVIEW.SHIPBYDATE',
						dataCell: DateTimeCellData,
						dateFormat: 'L',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						sortable: true,
						dontConvertTime: true,
					},
					{
						key: 'channelDeliverByDate',
						title: 'ORDERVIEW.DELIVERBYDATE',
						dataCell: DateTimeCellData,
						dateFormat: 'L',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						sortable: true,
						dontConvertTime: true,
					},
					{
						key: 'orderLines.sku',
						title: 'ORDERVIEW.ORDERLINESKU',
						selected: false,
						width: 200,
						filter: null,
						dataCell: QuantityPerSkuCellData,
						routeName: 'master.app.main.product.list.detail',
						filterType: TableFilterTemplate,
						sortable: true,
						service: 'orderLineService',
						dontSplit: true,
						fieldToDisplay: 'productQuantityPerSku',
					},
					{
						key: 'orderLines.quantity',
						title: 'ORDERVIEW.ORDERLINEQUANTITY',
						embed: ['orderLines'],
						service: 'orderLineService',
						dataCell: MultipleCellData,
						selected: false,
						width: 200,
						filter: null,
						dontSplit: true,
						filterType: TableFilterNumericTemplate,
						sortable: true,
					},
					{
						key: 'trackingNumbers',
						title: 'ORDERVIEW.TRACKINGNUMBER',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						embed: ['fulfillmentOrders, fulfillmentOrders.shipmentInfos'],
						service: 'shipmentInfoService',
						dontSplit: true,
					},
					{
						key: 'shippingCoreCountryState.name',
						title: 'ORDERVIEW.STATE',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						lookupConstant: lookupRouteName.state,
						dontSplit: true,
					},
					{
						key: 'channel.name',
						title: 'ORDERVIEW.CHANNEL',
						service: 'channelService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'channel.channelType.name',
						title: 'ORDERVIEW.CHANNELTYPE',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						lookupConstant: lookupRouteName.channelType,
					},
					{
						key: 'seller.applicationTitle',
						title: 'ORDERVIEW.SELLER',
						service: 'applicationService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						enabled: isSuperAdminOrganization(rootStore),
						filterApiParams: {
							sellerOnly: true,
						},
					},
					{
						key: 'orderType.name',
						title: 'ORDERVIEW.ORDERTYPE',
						selected: false,
						sortable: true,
						width: 200,
						filterType: TableFilterTemplate,
						filter: null,
						lookupConstant: lookupRouteName.orderType,
						sortable: true,
					},
					{
						key: 'confirmationType.name',
						title: 'ORDERVIEW.CONFIRMATIONTYPE',
						selected: false,
						sortable: true,
						width: 200,
						filterType: TableFilterTemplate,
						filter: null,
						lookupConstant: lookupRouteName.confirmationType,
					},
					{
						key: 'orderStatus.name',
						title: 'ORDERVIEW.ORDERSTATUS',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						lookupConstant: lookupRouteName.orderStatus,
					},
					{
						key: 'isPrime',
						title: 'ORDERVIEW.ISPRIME',
						dataCell: BooleanCellData,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
					},
					{
						key: 'partnerPO',
						title: 'ORDERVIEW.REFERENCENUMBER',
						service: 'orderService',
						selected: false,
						width: 200,
						filterType: TableFilterTemplate,
						filter: null,
						isClickable: true,
						sortable: true,
						filterApiParams: {
							isWarehouse: isWarehouse(rootStore),
						},
					},
					{
						key: 'orderLines.isBuyerRequestedCancel',
						title: 'ORDERVIEW.ISBUYERREQUESTEDCANCEL',
						dataCell: BuyerRequestedCancelCellData,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						service: 'orderLines',
					},
					{
						key: 'requiredWarehouse',
						title: 'ORDERVIEW.REQUIREDWAREHOUSE',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
					},
					{
						key: 'cancelReason.name',
						title: 'ORDERVIEW.CANCELREASON',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						lookupConstant: lookupRouteName.cancelReason,
					},
					{
						key: 'onHoldReason.name',
						title: 'ORDERVIEW.ONHOLDREASON',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						lookupConstant: lookupRouteName.onHoldReason,
					},
					{
						key: 'addressValidationErrors',
						title: 'ORDERVIEW.ADDRESSVALIDATIONERRORS',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: false,
					},
				],
				actions: {
					onSplit: async item => this.openConfirmationModal(this.getSplitConfirmModalProps(item)),
					onEdit: async item => this.routes.edit(item.id),
					onCancelOrder: async item => this.openConfirmationModal(this.getCancelOrderLinesConfirmModalProps(item)),
					onCancelMarketplaceOrder: async item =>
						this.openModal({
							onSuccess: async () => await this.queryUtility.initialize(false),
							data: { selectedItemId: item.id, isMarketplaceOrder: true },
							component: Cancel,
						}),
					onUncancelMarketplaceOrder: async item =>
						this.openConfirmationModal(this.getUncancelOrderConfirmModalProps(item)),
					onCreateReturnLabel: async item => this.openCreateReturnLabelPage(item),
					onReplace: async item =>
						this.openModal({
							onSuccess: async () => await this.findOrder(),
							data: { selectedItemId: item.id, parentView: this },
							component: Replacement,
						}),
					marketplaceOrderOnHold: async item =>
						this.openModal({
							onSuccess: async () => await this.queryUtility.initialize(false),
							data: { id: item.id },
							component: MarketplaceOrderOnHold,
						}),
				},
				sortObject: [
					{
						field: 'orderNumber',
						dir: 'asc',
					},
				],
			},
			routes: {
				list: () => this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list', {}),
				create: () => this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list.add', {}),
				edit: id =>
					this.rootStore.routerStore.goTo('master.app.main.order.list.edit', {
						id,
					}),
			},
			permissions: {
				onSplit: () => this.authStore.hasPermission('marketplaceOrderSection.full'),
				onEdit: () => this.authStore.hasPermission('marketplaceOrderSection.update'),
				onCancelOrder: () => this.authStore.hasPermission('marketplaceOrderSection.full'),
				onCancelMarketplaceOrder: () => this.authStore.hasPermission('marketplaceOrderSection.full'),
				onUncancelMarketplaceOrder: () => this.authStore.hasPermission('marketplaceOrderSection.full'),
				onCreateReturnLabel: () => this.authStore.hasPermission('marketplaceOrderSection.full'),
				onReplace: () => this.authStore.hasPermission('marketplaceOrderSection.full'),
				marketplaceOrderOnHold: () => this.authStore.hasPermission('marketplaceOrderSection.full'),
			},
		});
		makeObservable(this);
		if (isSuperAdminOrganization(rootStore)) {
			this.isSuperAdmin = true;
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
			this.orderImportForm.$('sellerId').set('rules', 'required');
		}
		this.setInitialFiltering();
		this.queryUtility.initialize(this.findOrder);
		checkForColumnFilter(localStorageHandler.get('marketplace-order-table-data'), this.tableData.columns);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.columnFilterSetup = 'marketplace-order-table-data';
		this.hasPiiAccess = this.rootStore.authStore.hasPiiAccess;
		this.lookupStore = new LookupStore(rootStore);
		this.orderLineStore = new OrderLineStore(rootStore);
		this.removeFiltersModal = new RemoveFiltersModalUtility({ viewStore: this });
		this.orderStore = new OrderStore(rootStore);
		this.isWarehouse = isWarehouse(rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.tableFilter = new TableFilterUtility({
			lookupStore: this.lookupStore,
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			services: [
				this.listStore.service,
				new OrderLineService(this.rootStore.app.baasic.apiClient),
				new ChannelService(this.rootStore.app.baasic.apiClient),
				new ProductVirtualSkuService(this.rootStore.app.baasic.apiClient),
				new ShipmentInfoService(this.rootStore.app.baasic.apiClient),
				new ApplicationService(this.rootStore.app.baasic.apiClient),
			],
		});
		this.setButtons();
		this.setHeaderModes();
		this.setSearchVisible();
	}

	openProductPage = productId => {
		this.rootStore.routerStore.goTo('master.app.main.product.list.detail', {
			id: productId,
		});
	};

	openDetailPage = id => {
		this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list.detail', {
			id: id,
		});
	};

	@action.bound
	setInitialFiltering = async () => {
		const currentFiltering = localStorageHandler.get('marketplace-orders-filter');
		this.showTodayShipments = currentFiltering != null ? currentFiltering.showTodayShipments : true;
		this.showUnlinkedOrders = currentFiltering != null ? currentFiltering.showUnlinkedOrders : false;
	};

	@action.bound
	cancelAllOrderLines = async item => {
		this.suspend();
		try {
			const {
				data: { total, canceled, refunded, message },
			} =
				item.channel.channelType.name == channelType.amazon
					? await this.orderLineStore.cancelAmazonOrder(item.id, sourceOfChange.cancelAllOrderLines)
					: await this.orderLineStore.cancelMiraklOrder(item.id, sourceOfChange.cancelAllOrderLines);

			if (message) {
				this.rootStore.notificationStore.error(message);
			} else {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.CANCELORDERSSUCCESS',
					{ total, canceled, failed: total - (canceled + refunded) },
				]);
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	uncancelMarketplaceOrder = async item => {
		this.suspend();
		try {
			const response = await this.orderStore.uncancelMarketplaceOrder(
				{ orderId: item.id },
				sourceOfChange.uncancelFulfillmentOrder
			);

			if (response.result == 'success') {
				this.rootStore.notificationStore.success('Order uncanceled successfully.', false, {
					autoClose: true,
					onClick: () => {
						this.openModal({
							data: response,
							component: CancelUncancelResult,
						});
					},
				});
			} else if (response.result == 'partial') {
				this.rootStore.notificationStore.warning('Order partially uncanceled. Click here for more details.', false, {
					autoClose: false,
					onClick: () => {
						this.openModal({
							data: response,
							component: CancelUncancelResult,
						});
					},
				});
			} else {
				this.rootStore.notificationStore.error('Order uncancel failed. Click here for more details.', false, {
					autoClose: false,
					onClick: () => {
						this.openModal({
							data: response,
							component: CancelUncancelResult,
						});
					},
				});
			}
			await this.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	openImportOrdersModal = async () => {
		if (this.isSuperAdminOrganization) {
			this.sellerDropdownStore = new BaasicDropdownViewStore({
				field: this.orderImportForm.$('sellerId'),
				store: new ApplicationStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'applicationTitle',
				},
				filterProperties: {
					sellerOnly: true,
				},
				value: localStorageHandler.get('selectedSeller'),
				isRequired: true,
			});
		}
		this.openModal({
			onClose: () => {
				this.orderImportForm.$('sellerId').reset();
			},
			component: OrderImport,
		});
	};

	@action.bound
	async downloadExcelTemplate() {
		const workbook = new excelJS.Workbook();

		workbook.created = new Date();
		workbook.modified = new Date();

		let sheet = workbook.addWorksheet('ImportOrdersTemplate');

		sheet.columns = [
			{ header: 'OrderNumber', key: 'OrderNumber', width: 15 },
			{ header: 'OrderDate', key: 'OrderDate', width: 15 },
			{ header: 'ShipByDate', key: 'ShipByDate', width: 15 },
			{ header: 'BuyerName', key: 'BuyerName', width: 15 },
			{ header: 'Phone', key: 'Phone', width: 15 },
			{ header: 'ShippingAddress1', key: 'ShippingAddress1', width: 20 },
			{ header: 'ShippingAddress2', key: 'ShippingAddress2', width: 20 },
			{ header: 'ShippingCity', key: 'ShippingCity', width: 15 },
			{ header: 'ShippingZipCode', key: 'ShippingZipCode', width: 20 },
			{ header: 'ShippingCountry', key: 'ShippingCountry', width: 20 },
			{ header: 'ShippingState', key: 'ShippingState', width: 15 },
			{ header: 'Sku', key: 'Sku', width: 15 },
			{ header: 'Quantity', key: 'Quantity', width: 15 },
			{ header: 'RequiredWarehouse', key: 'RequiredWarehouse', width: 20 },
			{ header: 'RequiredCarrierService', key: 'RequiredCarrierService', width: 20 },
			{ header: 'RequiredCarrier', key: 'RequiredCarrier', width: 20 },
		];

		var requiredFieldStyle = {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: 'FFC7CE' },
		};

		var borderStyle = {
			top: { style: 'medium', color: { argb: '27788a' } },
			left: { style: 'medium', color: { argb: '27788a' } },
			bottom: { style: 'medium', color: { argb: '27788a' } },
			right: { style: 'medium', color: { argb: '27788a' } },
		};

		sheet.getCell('A1').border = borderStyle;
		sheet.getCell('B1').border = borderStyle;
		sheet.getCell('C1').border = borderStyle;
		sheet.getCell('D1').border = borderStyle;
		sheet.getCell('E1').border = borderStyle;
		sheet.getCell('F1').border = borderStyle;
		sheet.getCell('G1').border = borderStyle;
		sheet.getCell('H1').border = borderStyle;
		sheet.getCell('I1').border = borderStyle;
		sheet.getCell('J1').border = borderStyle;
		sheet.getCell('K1').border = borderStyle;
		sheet.getCell('L1').border = borderStyle;
		sheet.getCell('M1').border = borderStyle;
		sheet.getCell('N1').border = borderStyle;
		sheet.getCell('O1').border = borderStyle;
		sheet.getCell('P1').border = borderStyle;

		sheet.getCell('A1').fill = requiredFieldStyle;
		sheet.getCell('D1').fill = requiredFieldStyle;
		sheet.getCell('F1').fill = requiredFieldStyle;
		sheet.getCell('H1').fill = requiredFieldStyle;
		sheet.getCell('I1').fill = requiredFieldStyle;
		sheet.getCell('J1').fill = requiredFieldStyle;
		sheet.getCell('K1').fill = requiredFieldStyle;
		sheet.getCell('L1').fill = requiredFieldStyle;

		sheet.getCell('B1').note = 'Must enter date in valid date format (MM/DD/YYYY)';
		sheet.getCell('C1').note = 'Must enter date in valid date format (MM/DD/YYYY)';
		sheet.getCell('J1').note = 'Must enter value in valid format (e.g. United States or US).';
		sheet.getCell('K1').note = 'Must enter value in valid format (e.g. Indiana or IN)';
		sheet.getCell('L1').note = 'Must enter value that exists in the system';

		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], { type: 'application/xlsx' });
		saveAs(blob, 'ImportOrdersTemplate.xlsx');
	}

	@action.bound onFilesDrop = async filesMeta => {
		let { isValid } = await this.orderImportForm.validate();
		if (!isValid) {
			this.orderImportForm.showErrors(true);
			return;
		}
		if (filesMeta && filesMeta.length == 1) {
			let fileMeta = filesMeta[0];
			let reader = new FileReader();
			let xlsx = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
			let xls = 'application/vnd.ms-excel';
			if (
				fileMeta.type === xlsx ||
				(fileMeta.type === xls && !fileMeta.name.includes('csv', fileMeta.name.length - 3))
			) {
				//Excel processing (excluding csv)
				reader.onload = async evt => {
					this.suspend();
					const workbook = XLSX.read(evt.target.result, { type: 'binary' });
					const firstWorksheet = workbook.Sheets[workbook.SheetNames[0]];
					let data = XLSX.utils.sheet_to_json(firstWorksheet);
					if (data.length > 0) {
						await this.createOrdersFromExcel(data);
						await this.findOrder();
					} else {
						this.rootStore.notificationStore.error(['ORDERVIEW.NOVALIDDATAFORIMPORTFOUND']);
					}
					this.resume();
				};
				try {
					reader.readAsBinaryString(fileMeta);
				} catch (err) {}
			}
		}
	};

	validateRequiredFields = item => {
		if (
			item.hasOwnProperty('OrderNumber') &&
			item.hasOwnProperty('BuyerName') &&
			item.hasOwnProperty('ShippingAddress1') &&
			item.hasOwnProperty('ShippingCity') &&
			item.hasOwnProperty('ShippingZipCode') &&
			item.hasOwnProperty('ShippingCountry') &&
			item.hasOwnProperty('Sku')
		) {
			return true;
		} else {
			return false;
		}
	};

	@action.bound
	createOrdersFromExcel = async data => {
		let ordersToCreate = [];
		let orderNumbersForImport = [...new Set(data.map(x => x.OrderNumber))];

		// Group by OrderNumber, then create an order for each group
		let groupedOrders = _.groupBy(data, 'OrderNumber');
		_.forEach(groupedOrders, group => {
			let orders = [];
			let groupHasError = false;
			for (let order of group) {
				if (groupHasError) {
					break;
				}
				const isOrderValid = this.validateRequiredFields(order);
				if (!isOrderValid) {
					groupHasError = true;
					break;
				}
				let orderDate = new Date(Date.UTC(0, 0, order.OrderDate - 1));
				let shipByDate = new Date(Date.UTC(0, 0, order.ShipByDate - 1));
				if ((order.OrderDate && !moment(orderDate).isValid()) || (order.ShipByDate && !moment(shipByDate).isValid())) {
					groupHasError = true;
					break;
				}

				const regex = /^\d+$/;
				if (order.Quantity && !String(order.Quantity).match(regex)) {
					groupHasError = true;
					break;
				}

				if (order.Phone != null && !String(order.Phone).match(regex)) {
					order.Phone = String(order.Phone).replace(/\D/g, '');
				}

				orders.push(order);
			}

			if (!groupHasError && orders.length > 0) {
				let orderDate;
				let shipByDate;
				let currentDate = new Date();
				let tomorrow = new Date();
				currentDate.setHours(0, 0, 0, 0);
				tomorrow.setDate(currentDate.getDate() + 1);
				tomorrow.setHours(0, 0, 0, 0);

				if (orders[0].OrderDate) {
					let date = excelDateToJSDate(orders[0].OrderDate);
					date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
					orderDate = moment.utc(moment(date)).format();
				} else {
					orderDate = moment.utc(moment(currentDate)).format();
				}

				if (orders[0].ShipByDate) {
					shipByDate = excelDateToJSDate(orders[0].ShipByDate);
				} else {
					shipByDate = tomorrow.toLocaleString();
				}

				let masterOrder = {
					orderNumber: orders[0].OrderNumber,
					orderDate: orderDate,
					shipByDate: shipByDate,
					paymentDate: orderDate,
					buyerName: orders[0].BuyerName,
					buyerPhone: orders[0].Phone != null ? String(orders[0].Phone) : '',
					shippingAddress1: orders[0].ShippingAddress1,
					shippingAddress2: orders[0].ShippingAddress2 != null ? orders[0].ShippingAddress2 : '',
					shippingCity: orders[0].ShippingCity,
					requiredWarehouse: orders[0].RequiredWarehouse,
					requiredCarrierService: orders[0].RequiredCarrierService,
					requiredCarrierName: orders[0].RequiredCarrier,
					shippingZipCode: orders[0].ShippingZipCode,
					shippingCoreCountry: {
						name: orders[0].ShippingCountry,
					},
					shippingCoreCountryState: {
						name: orders[0].ShippingState,
					},
					sellerId: this.isSuperAdmin ? this.orderImportForm.$('sellerId').value : '',
					orderLines: [],
				};

				for (let order of orders) {
					masterOrder.orderLines.push({
						quantity: order.Quantity ? order.Quantity : 1,
						sku: order.Sku,
					});
				}
				ordersToCreate.push(masterOrder);
			}
		});

		if (ordersToCreate.length > 0) {
			var createdOrders = await this.listStore.createOrdersBulk(ordersToCreate, sourceOfChange.createOrderFromXLS);
			if (createdOrders == null) {
				this.rootStore.notificationStore.error(['ORDERVIEW.ORDERIMPORT.FAIL']);
			} else {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.ORDERIMPORT.SUCCESS',
					{ successful: createdOrders.length, total: orderNumbersForImport.length },
				]);
			}
		} else {
			this.rootStore.notificationStore.error(['ORDERVIEW.ORDERIMPORT.FAIL']);
		}
	};

	splitMarketplaceOrder = async item => {
		this.suspend();
		try {
			var response = await this.listStore.splitMarketplaceOrder(item.id, sourceOfChange.splitOrderList);
			if (response[0].isSuccess) {
				response[0].inactiveOrderLineIds.length === 0
					? this.rootStore.notificationStore.success(['ORDERVIEW.SPLITMARKETPLACEORDERSUCCESS'])
					: this.rootStore.notificationStore.warning([
							'ORDERVIEW.SPLITMARKETPLACEORDERPARTIALSUCCESS',
							{
								successful: item.orderLines.length - response[0].inactiveOrderLineIds.length,
								total: item.orderLines.length,
							},
					  ]);
			} else {
				this.rootStore.notificationStore.error([
					response[0].message ? response[0].message : 'ORDERVIEW.ORDERINQUEUEFORSPLIT',
				]);
			}
			await this.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	setAsChanged = () => {
		this.change = !this.change;
	};

	@action.bound
	linkOrderLines = async () => {
		this.suspend();
		try {
			const response = await this.orderLineStore.linkAllOrderLines(sourceOfChange.linkAllOrderLines);
			if (response.data != 0) {
				this.rootStore.notificationStore.success(['ORDERVIEW.LINKORDERLINESSUCCESS', { count: response.data }]);
				this.rootStore.viewStore.countUnlinkedOrderLines();
				await this.queryUtility.initialize(false);
			} else {
				this.rootStore.notificationStore.error('ORDERVIEW.NONEWLINKS');
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	setButtons = () => {
		const buttons = {
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-cog spc--right--sml',
					onClick: this.baasicTableSettingsViewStore.openTableSettingsModal,
					text: 'SETTINGS',
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-trash spc--right--sml',
					onClick: () => {
						this.removeFiltersModal.handleRemoveFiltersButtonClick();
					},
					text: 'FILTER.REMOVEALL',
					disabled: !!(this.queryUtility.filter.advancedSearch === undefined),
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-link spc--right--sml',
					onClick: () => this.openConfirmationModal(this.getLinkOrderLinesConfirmModalProps()),
					text: 'ORDERVIEW.LINKORDERLINES',
				},
			],
		};
		if (this.hasPiiAccess) {
			if (!this.isWarehouse) {
				buttons.buttons.unshift({
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-file-import spc--right--sml',
					onClick: this.openImportOrdersModal,
					text: 'ORDERVIEW.IMPORTORDERS',
				});
			}
			buttons.buttons.unshift({
				className: 'btn btn--dropdown header__button',
				iconClassName: 'fa fa-plus spc--right--sml',
				onClick: this.routes.create,
				authorization: 'marketplaceOrderSection.create',
				text: 'ORDERVIEW.NEW',
			});
		}
		this.setButtonComponent(buttons);
	};

	@action.bound
	expandChange = event => {
		const data = this.listStore.data;
		const item = _.find(data, ({ id }) => id === event.dataItem.id);
		item.expanded = !item.expanded;
		this.listStore.setResponseItem(data);
		this.setAsChanged();
	};

	@action.bound
	toggle = selected => {
		this.showOrderLines = selected;
	};

	@action.bound
	onCheck = async check => {
		this.showTodayShipments = check.currentTarget.checked;
		await this.findOrder();
	};

	@action.bound
	onShowUnlinkedOrdersCheck = async check => {
		this.showUnlinkedOrders = check.currentTarget.checked;
		await this.findOrder();
	};

	@action.bound
	findOrder = async () => {
		this.suspend();
		if (this.baasicTableSettingsViewStore == undefined) {
			this.baasicTableSettingsViewStore = new BaasicTableSettingsViewStore(this.rootStore);
		}
		await this.baasicTableSettingsViewStore.setTemplates(
			this.tableData,
			'MarketplaceOrdersTableSettings',
			this.setAsChanged,
			this.findOrder
		);
		this.queryUtility.setFilterParams(
			this,
			{
				checkPartiallyFulfillMixedOrders: true,
				fetchActiveShipments: true,
				showTodayShipments: this.showTodayShipments,
				showUnlinkedOrders: this.showUnlinkedOrders,
			},
			this.tableFilter
		);
		await this.listStore.findOrder(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.queryUtility.setFilterPersistance(this, 'marketplace-orders-filter', 'marketplace-order-table-data');
		this.setButtons();
		this.resume();
	};

	getLinkOrderLinesConfirmModalProps = () => {
		return {
			onConfirm: async () => await this.linkOrderLines(),
			message: ['ORDERVIEW.LINKORDERLINESCONFIRM'],
		};
	};

	getSplitConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.splitMarketplaceOrder(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['ORDERVIEW.SPLITMARKETPLACEORDER', { orderNumber: item.orderNumber }],
			data: item,
		};
	};

	getCancelOrderLinesConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.cancelAllOrderLines(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['ORDERVIEW.CANCELALLORDERLINESCONFIRM'],
			data: item,
		};
	};

	getUncancelOrderConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.uncancelMarketplaceOrder(item),
			message: ['ORDERVIEW.UNCANCELORDER', { orderNumber: item.orderNumber }],
			data: item,
		};
	};

	@action.bound
	openCreateReturnLabelPage = item => {
		this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list.return-label', {
			id: item.id,
		});
	};
}

export default MarketplaceOrderListViewStore;
